import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { RadioGroup } from "@cayuse/shared-react.components.form.radio-group";
import IconButton from "@cayuse/shared-react.components.icon-button";
import { isNonEmptyArray, isNonEmptyObject } from "@cayuse/shared-react.utils.type-checks";
import { api } from "services";
import { v4 as uuidv4 } from "uuid";

import { withFlowableStatefulFormComponent } from "components/flowable/wrappers/FlowableStatefulFormComponentWrapper";
import { RESEARCH_TEAM } from "globals/constants/formObjects";
import useDictionary from "hooks/useDictionary";
import { fetchPicklistOptions } from "store/actions/picklists";
import { picklistsSelectors } from "store/selectors";
import { findOrganizationById, findUnitById } from "utils";

import { initialAssociation, initialMember } from "./constants";
import useDisplayConditions from "./custom-hooks/useDisplayConditions";
import useEffortFormat from "./custom-hooks/useEffortFormat";
import { RTMember } from "./RTMember";
import styles from "./styles.css";
import {
	calculateCompleteCredit,
	createNewAssociationIfNeededAndRemoveEmpty,
	deepCopy,
	hasPrincipalInvestigatorFn,
	isPresent,
	convertAssociations,
	cleanupForPayload,
	isPrincipalInvestigator,
	convertExternalAssociations,
	createNewExtAssociationIfNeededAndRemoveEmpty,
	researchTeamPayloadChanged,
} from "./utils";


const { EFFORT_FORMAT, ASSOCIATION_TYPE } = RESEARCH_TEAM;

const getEffortToggleOptions = dictionary => {
	return [
		{
			label: dictionary.effortFormatPercentage || "Percentage",
			value: EFFORT_FORMAT.PERCENTAGE,
		},
		{
			label: dictionary.effortFormatMonths || "Months",
			value: EFFORT_FORMAT.MONTHS,
		}
	];
};

const ResearchTeam = props => {
	const [ state, setState ] = useState({
		touched: false,
		formValid: true,
		loading: true,
		hasPrincipalInvestigator: false,
		completeCredit: 0,
		memberToAssociationLookupMap: {},
		initialLoad: true,
		selectedAssociationsDetails: [],
	});

	const [ teamMembers, setTeamMembers ] = useState([]);
	const initialized = useRef(false);

	const {
		Components,
		onChange,
		config: {
			enabled,
			value: configValue,
			// Remove default values once migration is done
			extraSettings: {
				externalResearchers = true,
				creditField = true,
				creditFieldRequired = false,
				sponsoredEffort = true,
				sponsoredEffortRequired = false,
				costShareEffort = true,
				costShareEffortRequired = false,
				defaultEffortFormat = EFFORT_FORMAT.PERCENTAGE,
			},
			customValidations = []
		},
	} = props;

	const dictionary = useDictionary("researchTeam") || {};

	const [ effortFormat, setEffortFormat ] = useEffortFormat(defaultEffortFormat);
	const [ displayConditions ] = useDisplayConditions(
		{ externalResearchers, creditField, creditFieldRequired, sponsoredEffort, sponsoredEffortRequired, costShareEffort, costShareEffortRequired },
		teamMembers,
		props.config?.isRequired,
	);

	const researchTeamRolesObject = useSelector(picklistsSelectors.researchTeamRoles);

	const roles = researchTeamRolesObject.options || [];

	const formEntityIsFetching = useSelector(state => state.form?.entity?.isFetching);

	useEffect(() => {
		// Init from the value array; ignore Flowable's transient null/undefined emits.
		if (!Array.isArray(props.config?.value)) {
			return;
		}
		if (initialized.current && !researchTeamPayloadChanged(teamMembers, props.config?.value)) {
			return;
		}
		initialized.current = true;
		init();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ props.config ]);

	useEffect(() => {
		// Empty form: render a blank row only once the form finished loading with no value.
		if (initialized.current || formEntityIsFetching || Array.isArray(props.config?.value)) {
			return;
		}
		initialized.current = true;
		init();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ formEntityIsFetching, props.config ]);

	const loadAssociationsDetails = async persons => {
		let personsAssociationsIds = [],
			personsExternalAssociationsIds = [],
			selectedExternalAssociationIds = [],
			selectedAssociationIds = [];

		if (isNonEmptyArray(persons)) {
			personsAssociationsIds = persons
				.filter(person => isNonEmptyObject(person) && isPresent(person.associations))
				.flatMap(v => v.associations)
				.map(a => a.id)
				.filter(Boolean);

			personsExternalAssociationsIds = persons
				.filter(person => isNonEmptyObject(person) && isPresent(person.externalAssociations))
				.flatMap(v => v.externalAssociations)
				.map(a => a.organizationId)
				.filter(Boolean);
		}

		if (isNonEmptyArray(configValue)) {
			selectedAssociationIds = configValue
				.flatMap(v => v.associations)
				.map(a => a.association)
				.filter(Boolean);

			selectedExternalAssociationIds = configValue
				.filter(member => isPresent(member.externalAssociations))
				.flatMap(v => v.externalAssociations)
				.map(a => a.association)
				.filter(Boolean);
		}

		const requests = selectedAssociationIds.concat(personsAssociationsIds)
			//remove duplicates
			.filter((c, index, arr) => arr.indexOf(c) === index)
			.map(id => findUnitById(id));

		const requestsExternal = selectedExternalAssociationIds.concat(personsExternalAssociationsIds)
			.filter((c, index, arr) => arr.indexOf(c) === index)
			.map(id => findOrganizationById(id));

		const response = await Promise.allSettled([ ...requests, ...requestsExternal ]);

		const selectedAssociationsDetails = [];
		for (const responseObject of response) {
			if (responseObject.status === "fulfilled") {
				if (responseObject.value) {
					selectedAssociationsDetails.push(responseObject.value);
				}
			}
		}

		setState(prevState => ({ ...prevState, selectedAssociationsDetails }));

		return selectedAssociationsDetails;
	};

	const getRoles = item => {
		const { hasPrincipalInvestigator } = state;
		const selectedOrActiveRoles = roles.filter(({ id, active }) => id === item.role || active);

		const filterPI = () => selectedOrActiveRoles.filter(({ id }) => id !== "principal-investigator");

		return isNonEmptyArray(selectedOrActiveRoles) && hasPrincipalInvestigator && !isPrincipalInvestigator(item)
			? filterPI()
			: [ ...selectedOrActiveRoles ];
	};

	const lookupAssociations  = async values => {
		const members = values.filter(value => isPresent(value.member));

		const personRequests = members.map(entry => api.person.get({ id: entry.member, includeInactiveAffiliations: true }));

		const persons = await Promise.all(personRequests);

		// We are not awaiting for this promise to resolve
		// but we are keeping the reference to it incase we find that there
		// are removed associations in Admin module
		const associationDetails = loadAssociationsDetails(persons);

		const memberToAssociationLookupMap = {};

		for (const person of persons) {
			const personAssociations = convertAssociations(person.associations),
				personExternalAssociations = convertExternalAssociations(person.externalAssociations);

			const removedAssociations = members
				.filter(member => member.member === person.id && member.associationType === ASSOCIATION_TYPE.INTERNAL)
				.flatMap(member => member.associations)
				.filter(association => !personAssociations.find(a => a.id === association.association));

			const removedExternalAssociations = members
				.filter(member => member.member === person.id && member.associationType === ASSOCIATION_TYPE.EXTERNAL)
				.flatMap(member => member.externalAssociations)
				.filter(association => association && !personExternalAssociations.find(a => a.id === association.association));

			for (const removedAssociation of removedAssociations) {
				const association = (await associationDetails).find(a => a.id === removedAssociation.association);

				if (!association) {
					console.warn(`Could not find association with id ${removedAssociation.association} for person ${person.id}`);
				} else {
					personAssociations.push({
						id: association.id,
						text: association.name,
						disabled: true,
						removed: true,
						associationType: ASSOCIATION_TYPE.INTERNAL,
					});
				}
			}

			for (const removedExternalAssociation of removedExternalAssociations) {
				const association = (await associationDetails).find(a => a.id === removedExternalAssociation.association);

				if (!association) {
					console.warn(`Could not find association with id ${removedExternalAssociation.association} for person ${person.id}`);
				} else {
					personExternalAssociations.push({
						id: association.id,
						text: association.name,
						disabled: true,
						associationType: ASSOCIATION_TYPE.EXTERNAL,
					});
				}
			}

			memberToAssociationLookupMap[person.id] = [ ...personAssociations, ...personExternalAssociations ];
		}

		return memberToAssociationLookupMap;
	};

	const init = () => {
		setState(prevState => ({ ...prevState, loading: true }));
		const associationDetailsPromise = loadAssociationsDetails(configValue);

		const initialValue = isNonEmptyArray(configValue)
			? configValue.map(member => ({
				...member,
				associationType: member.associationType || ASSOCIATION_TYPE.INTERNAL,
				isInitialCalculationRequired: member.isInitialCalculationRequired ?? true,
				key: uuidv4()
			}))
			: [ initialMember ];
		for (const member of initialValue) {
			if(member.associationType === ASSOCIATION_TYPE.INTERNAL) {
				member.associations = createNewAssociationIfNeededAndRemoveEmpty(initialAssociation, member.associations);
			} else if (member.associationType === ASSOCIATION_TYPE.EXTERNAL && member.externalAssociations) {
				member.externalAssociations = createNewExtAssociationIfNeededAndRemoveEmpty(member.externalAssociations);
			}
		}
		// Update synchronously so a follow-up [props.config] effect skips duplicate init.
		const initialTeamMembers = deepCopy(initialValue);
		setTeamMembers(initialTeamMembers);

		const roleIds = initialTeamMembers.map(teamMember => teamMember.role);
		fetchPicklistOptions("researchTeamRoles", { data: roleIds });

		const hasPrincipalInvestigator = hasPrincipalInvestigatorFn(initialValue);
		const completeCredit = calculateCompleteCredit(initialValue);
		// Await both lookups so the form renders fully populated, not field-by-field.
		Promise.all([ lookupAssociations(initialValue), associationDetailsPromise ])
			.then(([ memberToAssociationLookupMap ]) => {
				setState(prevState => ({
					...prevState,
					successful: true,
					loading: false,
					hasPrincipalInvestigator,
					completeCredit,
					memberToAssociationLookupMap,
					initialLoad: isNonEmptyArray(configValue) ? false : true,
				}));
			});
	};

	const updateValue = (onChange, setValue, shouldUpdateAssociations) => {
		const members = deepCopy(teamMembers);
		setValue(members);
		props.dispatchFocusEvent();
		onChange(cleanupForPayload(members));

		const hasPrincipalInvestigator = hasPrincipalInvestigatorFn(members);
		const completeCredit = calculateCompleteCredit(members);

		if (shouldUpdateAssociations) {
			lookupAssociations(members)
				.then(memberToAssociationLookupMap => {
					setState(prevState => ({
						...prevState,
						hasPrincipalInvestigator,
						completeCredit,
						memberToAssociationLookupMap,
						initialLoad: false,
					}));
				});
		} else {
			setState(prevState => ({
				...prevState,
				hasPrincipalInvestigator,
				completeCredit,
				memberToAssociationLookupMap: state.memberToAssociationLookupMap,
				initialLoad: false,
			}));
		}
		setTeamMembers(members);
	};

	const addEntry = () => {
		const newMember = {
			...initialMember,
			associations: [ { ...initialAssociation } ],
			externalAssociations: [],
			key: uuidv4(),
		};
		updateValue(props.onChange, value => value.push(newMember));
	};

	// Load complete association object data for all associations that are used in the component

	const effortTypes = [ sponsoredEffort && "sponsoredEffort", costShareEffort && "costShareEffort" ].filter(Boolean);
	const {
		completeCredit,
		loading,
		initialLoad,
		members,
		memberToAssociationLookupMap,
		selectedAssociationsDetails,
	} = state;

	if (loading) {
		return <div>{dictionary.loading || "Loading..."}</div>;
	}

	const bem = props.bem("panel", { disabled: !enabled });

	const customValidationErrors = customValidations
		.filter(validation => !validation.expression)
		.map(validation => validation.errorMsg);

	return (
		<div className={bem()}>
			<div className={styles.rtLabel}>
				<Components.label
					{...props}
					config={props.config}
					className={`${
						props.config.isRequired
							? "flw__component--required__label"
							: ""
					}`}
				/>
			</div>
			<Components.description {...props} />
			<div className={styles.rtHeader}>
				<div>
					{displayConditions.shouldDisplayEffortToggle &&
						<RadioGroup
							label={dictionary.effortFormatToggleLabel || "View Effort"}
							className={styles.radioGroup}
							value={effortFormat}
							onChange={value => setEffortFormat(value)}
							options={getEffortToggleOptions(dictionary)}
						/>
					}
				</div>
				{displayConditions.shouldDisplayTotalCreditReadout &&
					<div className={completeCredit < 100 || initialLoad ? null : [ styles.error ]}>
						<p>
							<strong>{completeCredit}{dictionary.errors?.credit || "% of total credit has been allocated"}</strong><br/>
							{isNonEmptyArray(configValue) && completeCredit > 100}
						</p>
					</div>
				}
			</div>
			{teamMembers.map((item, index) => (
				<RTMember
					key={item.key}
					index={index}
					item={item}
					bem={bem}
					config={props.config}
					Components={Components}
					onChange={onChange}
					teamMembers={teamMembers}
					members={members}
					memberToAssociationLookupMap={memberToAssociationLookupMap}
					selectedAssociationsDetails={selectedAssociationsDetails}
					getRoles={getRoles}
					updateValue={updateValue}
					restProps={props}
					isPresent={isPresent}
					dictionary={dictionary}
					effortFormat={effortFormat.toLowerCase()}
					effortTypes={effortTypes}
					displayConditions={displayConditions}
					associationType={item.associationType?.toLowerCase() || ASSOCIATION_TYPE.INTERNAL}
					hasPrincipalInvestigator={state.hasPrincipalInvestigator}
					touched={state.touched}
					setTouched={() => setState(prevState => ({ ...prevState, touched: true }))}
				/>
			))}
			<div className={`${bem("row")} ${styles.researchTeamUtilityBar}`}>
				<div className={bem(`col--6`)}>
					<p>
						<IconButton
							icon="plus"
							disabled={!enabled}
							onClick={addEntry}
							labelPosition="left"
							primary
							className={styles.marginLeft}
						>
							{dictionary.addTeamMemberButton || "Add Team Member"}
						</IconButton>
					</p>
				</div>
			</div>
			<div>
				{state.touched && customValidationErrors.length > 0 &&
					customValidationErrors.map((error, index) => (
						<span key={index} className={styles.customError}>
							{error}
						</span>
					))
				}
			</div>
		</div>
	);
};

export default withFlowableStatefulFormComponent(ResearchTeam);
